import { unreachable } from '@chainflip/utils/assertion';
import {
  type BaseChainflipAsset,
  type ChainflipAsset,
  type ChainflipChain,
  type ChainAssetMap,
  type InternalAssetMap,
  getInternalAsset,
  type AssetSymbol,
  chainflipAssets,
} from '@chainflip/utils/chainflip';
import { isTestnet } from '@/shared/featureFlags';
import { arbiscanUrl, etherscanUrl, solscanUrl } from './helpers';
import { assetOrder, chainOrder } from '../constants';

export const assetColors = {
  Eth: '#268AFF',
  Flip: '#FFFFFF',
  Usdc: '#70A4EC',
  Usdt: '#479079',
  Dot: '#FF33AF',
  Btc: '#FBCB9D',
  ArbUsdc: '#457DA7',
  ArbEth: '#9CD2E6',
  Sol: '#B695EF',
  SolUsdc: '#4941BA',
  HubDot: '#D996BF',
  HubUsdt: '#798DA8',
  HubUsdc: '#708A82',
} as const satisfies Record<ChainflipAsset, string>;

export const boostFeeTiers = [5, 10, 30];
export type BoostFeeTier = (typeof boostFeeTiers)[number];

export const buildChainflipExplorerLink = (
  blockIndex: string | undefined,
  path: 'events' | 'swaps' | 'channels' = 'events',
) => {
  if (!blockIndex) return undefined;

  return new URL(`/${path}/${blockIndex}`, process.env.NEXT_PUBLIC_EXPLORER_URL).toString();
};

export const buildExplorerLink = (
  chain: ChainflipChain | 'Chainflip',
  type: 'address' | 'block' | 'tx' | 'swap',
  value: string | number,
): string => {
  if (type === 'swap') {
    if (chain !== 'Chainflip') throw new Error('unsupported chain for swap link');
    if (!process.env.NEXT_PUBLIC_EXPLORER_URL) return `/swaps/${value}`;
    return new URL(`/swaps/${value}`, process.env.NEXT_PUBLIC_EXPLORER_URL).toString();
  }

  switch (chain) {
    case 'Bitcoin': {
      const baseUrl = `https://mempool.space${isTestnet ? '/testnet' : ''}`;
      return new URL(`/${type}/${value}`, baseUrl).toString();
    }
    case 'Assethub':
    case 'Polkadot': {
      const subdomain = chain === 'Assethub' ? 'assethub-polkadot' : 'polkadot';
      if (type === 'block') return `https://${subdomain}.subscan.io/block/${value}`;
      if (type === 'address') {
        return `https://${subdomain}.subscan.io/account/${value}`;
      }
      if (type === 'tx') {
        return `https://${subdomain}.subscan.io/extrinsic/${value}`;
      }
      return unreachable(type, 'unhandled link type');
    }
    case 'Ethereum':
      return etherscanUrl(type, value);
    case 'Arbitrum':
      return arbiscanUrl(type, value);
    case 'Solana':
      return solscanUrl(type, value);
    case 'Chainflip': {
      let path = '';
      if (type === 'address') path = `/lps/${value}`;
      if (type === 'block' || type === 'tx') throw new Error('unsupported');
      if (!process.env.NEXT_PUBLIC_EXPLORER_URL) return path;
      return new URL(path, process.env.NEXT_PUBLIC_EXPLORER_URL).toString();
    }
    default:
      return unreachable(chain, 'unhandled chain');
  }
};

export const isChainflipAsset = (asset: string | undefined): asset is ChainflipAsset =>
  chainflipAssets.includes(asset as ChainflipAsset);

export const isBaseChainflipAsset = (asset: string): asset is BaseChainflipAsset =>
  isChainflipAsset(asset) && asset !== 'Usdc';

export const chainAssetMapToInternalAssetMap = <T, U>(
  chainAssetMap: ChainAssetMap<T>,
  cb: (id: ChainflipAsset, value: T) => U,
) =>
  Object.fromEntries(
    Object.entries(chainAssetMap).flatMap(([chain, assetMap]) =>
      Object.entries(assetMap).map(([asset, value]) => {
        const id = getInternalAsset({
          chain: chain as ChainflipChain,
          asset: asset as AssetSymbol,
        });

        return [id, cb(id, value)];
      }),
    ),
  ) as InternalAssetMap<U>;

export const compareAssets = (a: ChainflipAsset, b: ChainflipAsset) =>
  assetOrder[a] - assetOrder[b];

export const compareChains = (a: ChainflipChain, b: ChainflipChain) =>
  chainOrder[a] - chainOrder[b];
